/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from "@tanstack/react-query";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import input from "../../../../../assets/styles/components/input.module.css";
import { loadAllSuppliers } from "../../../../../services/suppliers";
import type { IFinancialFilter } from "../../../../../types/financial";
import { formatDate } from "../../../../../utils/useMask";
import Button from "../../../../../components/Button/Button";
import SelectChartOfAccounts from "../../CashFlow/SelectChartOfAccounts";
import SelectCostCenter from "../../CashFlow/SelectCostCenter";
import SelectFinanceAccount from "../../CashFlow/SelectFinanceAccount";
import InputSuspense from "../../../../../components/InputSuspense/InputSuspense";
import ModalGeneric from "../../../../../components/ModalGeneric/ModalGeneric";
import styles from "../../AccountsReceivable/FinancialFilterModal/AddFinancialEntryModal.module.css";

interface FornecedorSelect {
  uid_fornecedor: string;
  nome_fantasia: string;
  razao_social?: string;
  nome?: string;
}

type props = {
  onClose: () => void;
  // Agora a tipagem aceita a nossa função que engatilha a atualização da URL
  setValuesInput: (filter: IFinancialFilter) => void;
  valuesInput: IFinancialFilter;
};

const PERIOD_OPTIONS = [
  { label: "Selecione um período", value: "" },
  { label: "Essa semana", value: "this_week" },
  { label: "Semana que vem", value: "next_week" },
  { label: "Semana passada", value: "last_week" },
  { label: "Esse Mês", value: "this_month" },
  { label: "Mês que vem", value: "next_month" },
  { label: "Mês passado", value: "last_month" },
  { label: "Esse ano", value: "this_year" },
  { label: "Ano que vem", value: "next_year" },
  { label: "Ano passado", value: "last_year" },
];

const ExpenseFilterModal = ({
  onClose,
  setValuesInput,
  valuesInput,
}: props) => {
  const initialTipo = "pagar";

  const [dataFilter, setDataFilter] = useState<
    IFinancialFilter & { period?: string }
  >({
    uid_fornecedor: valuesInput.uid_fornecedor || null,
    status: valuesInput.status || "",
    date_initial: valuesInput.date_initial,
    date_final: valuesInput.date_final,
    tipo: initialTipo,
    period: (valuesInput as any).period || null,
    uid_conta_financeira: valuesInput.uid_conta_financeira || null,
    uid_plano_contas: valuesInput.uid_plano_contas || null,
    uid_centro_custo: valuesInput.uid_centro_custo || null,
  });

  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [isActiveSupplier, setIsActiveSupplier] = useState(false);
  const [selectedSupplierName, setSelectedSupplierName] = useState("");

  const { data: suppliersData, isLoading: isLoadingSuppliers } = useQuery<
    any,
    Error
  >({
    queryKey: ["allSuppliersForFilter", supplierSearchTerm],
    queryFn: () => loadAllSuppliers(supplierSearchTerm, 1),
  });

  const suppliersForSelect: FornecedorSelect[] = useMemo(() => {
    if (!suppliersData) return [];

    return suppliersData?.data?.suppliers || [];
  }, [suppliersData]);

  useEffect(() => {
    if (dataFilter.uid_fornecedor) {
      const selectedSupplier = suppliersForSelect.find(
        (s) => s.uid_fornecedor === dataFilter.uid_fornecedor,
      );

      if (selectedSupplier) {
        const newName =
          selectedSupplier.nome_fantasia ||
          selectedSupplier.nome ||
          "Fornecedor";
        if (selectedSupplierName !== newName) {
          setSelectedSupplierName(newName);
        }
      } else {
        if (selectedSupplierName === "") {
          setSelectedSupplierName("Fornecedor Selecionado");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliersForSelect, dataFilter.uid_fornecedor]);

  const handlePeriodChange = (period: string) => {
    const today = new Date();
    const first = today.getDate() - today.getDay(); 

    const getOffsetDate = (offset: number) => {
      const d = new Date();
      d.setDate(first + offset);
      return formatDate(d);
    };

    let start: string | undefined = "";
    let end: string | undefined = "";

    switch (period) {
      case "this_week":
        start = getOffsetDate(0); 
        end = getOffsetDate(6); 
        break;
      case "next_week":
        start = getOffsetDate(7); 
        end = getOffsetDate(13); 
        break;
      case "last_week":
        start = getOffsetDate(-7); 
        end = getOffsetDate(-1); 
        break;
      case "this_month":
        ((start = formatDate(
          new Date(today.getFullYear(), today.getMonth(), 1),
        )),
          (end = formatDate(
            new Date(today.getFullYear(), today.getMonth() + 1, 0),
          )));
        break;
      case "next_month":
        ((start = formatDate(
          new Date(today.getFullYear(), today.getMonth() + 1, 1),
        )),
          (end = formatDate(
            new Date(today.getFullYear(), today.getMonth() + 2, 0),
          )));
        break;
      case "last_month":
        ((start = formatDate(
          new Date(today.getFullYear(), today.getMonth() - 1, 1),
        )),
          (end = formatDate(
            new Date(today.getFullYear(), today.getMonth(), 0),
          )));
        break;
      case "this_year":
        ((start = formatDate(new Date(today.getFullYear(), 0, 1))),
          (end = formatDate(new Date(today.getFullYear(), 11, 31))));
        break;
      case "next_year":
        ((start = formatDate(new Date(today.getFullYear() + 1, 0, 1))),
          (end = formatDate(new Date(today.getFullYear() + 1, 11, 31))));
        break;
      case "last_year":
        ((start = formatDate(new Date(today.getFullYear() - 1, 0, 1))),
          (end = formatDate(new Date(today.getFullYear() - 1, 11, 31))));
        break;
      default:
        return null;
    }

    setDataFilter((prev) => ({
      ...prev,
      period: period,
      date_initial: start,
      date_final: end,
    }));
  };

  const handleSelectSupplier = (nome: string, uid: string) => {
    setDataFilter((prev) => ({ ...prev, uid_fornecedor: uid }));
    setSelectedSupplierName(nome);
    setSupplierSearchTerm("");
    setIsActiveSupplier(false);
  };

  const handleSupplierInput = (inputValue: string) => {
    setSupplierSearchTerm(inputValue);
    setSelectedSupplierName(inputValue);
    setDataFilter((prev) => ({ ...prev, uid_fornecedor: "" }));
    setIsActiveSupplier(true);
  };

  const supplierOptionsMemo = useMemo(() => {
    if (isLoadingSuppliers)
      return <p className={styles.dropDownLoading}>Carregando...</p>;

    if (!suppliersForSelect || suppliersForSelect.length === 0) {
      return (
        <p className={styles.dropDownNoResults}>
          Nenhum fornecedor encontrado.
        </p>
      );
    }

    return suppliersForSelect.map((item) => (
      <p
        key={item.uid_fornecedor}
        className={styles.dropDownItem}
        onClick={() =>
          handleSelectSupplier(
            item.nome_fantasia || item.nome || "Sem Nome",
            item.uid_fornecedor,
          )
        }
      >
        {item.nome_fantasia || item.nome || "Sem Nome"}
      </p>
    ));
  }, [suppliersForSelect, isLoadingSuppliers]);

  const handleFilter = () => {
    const finalFilters = {
      ...dataFilter,
      tipo: dataFilter.tipo || initialTipo,
    };

    setValuesInput(finalFilters as IFinancialFilter);
    onClose();
  };

  const handleChange = (value: string, field: keyof IFinancialFilter) => {
    setDataFilter((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <ModalGeneric
      containerClass={styles.container}
      contentClass={styles.containerModalFilter}
      title="Filtrar Contas a Pagar"
      onClick={onClose}
    >
      <div className={styles.containerFilter}>
        <InputSuspense
          label="Fornecedor"
          placeholder="Pesquisar fornecedor"
          value={selectedSupplierName}
          onChange={(e) => handleSupplierInput(e.target.value)}
          isActiveDropDown={isActiveSupplier}
          setIsActiveDropDown={setIsActiveSupplier}
          option={supplierOptionsMemo}
          name="uid_fornecedor_input"
        />
        <input type="hidden" value={dataFilter.uid_fornecedor || ""} readOnly />

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <p>Status</p>
            <select
              className={input.input}
              value={dataFilter.status || ""}
              onChange={(e) =>
                setDataFilter((prev) => ({
                  ...prev,
                  status: e.target.value as IFinancialFilter["status"],
                }))
              }
            >
              <option value="">Todos Status</option>
              <option value="aberto">Em Aberto</option>
              <option value="pago">Pago</option>
              <option value="atrasado">Atrasado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

          <div style={{ flex: 1 }}>
            <SelectFinanceAccount
              handleChangeAccount={(value) => {
                handleChange(value, "uid_conta_financeira");
              }}
              value={dataFilter.uid_conta_financeira || ""}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <SelectCostCenter
              value={dataFilter.uid_centro_custo || ""}
              handleChangeCostCenter={(value) => {
                handleChange(value, "uid_centro_custo");
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectChartOfAccounts
              value={dataFilter.uid_plano_contas || ""}
              handleChangeChartOfAccount={(value) => {
                handleChange(value, "uid_plano_contas");
              }}
              type="ambos"
            />
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <p>Período</p>
            <select
              className={input.input}
              value={dataFilter.period || ""}
              onChange={(e) => handlePeriodChange(e.target.value)}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={styles.row}>
          <div style={{ flex: 1 }}>
            <p>Data Inicial (Vencimento)</p>
            <input
              type="date"
              className={input.input}
              value={dataFilter.date_initial || ""}
              onChange={(e) => {
                setDataFilter((prev) => ({
                  ...prev,
                  date_initial: e.target.value,
                  period: "",
                }));
              }}
            />
          </div>

          <div style={{ flex: 1 }}>
            <p>Data Final (Vencimento)</p>
            <input
              type="date"
              className={input.input}
              value={dataFilter.date_final || ""}
              onChange={(e) => {
                setDataFilter((prev) => ({
                  ...prev,
                  date_final: e.target.value,
                  period: "",
                }));
              }}
            />
          </div>
        </div>

        <Button
          variant="contain"
          onClick={handleFilter}
          disabled={isLoadingSuppliers}
        >
          {isLoadingSuppliers ? "Carregando..." : "Filtrar"}
        </Button>
      </div>
    </ModalGeneric>
  );
};

export default ExpenseFilterModal;